/**
 * ISO Certification Services page content (/iso-certification-services).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "ISO Certification Services (Main Page) — SEO Optimized 2026" requirement
 * document (MBA_MERN_Solutions/ISO Certification Services.docx). Do not reword,
 * shorten or extend this copy — update the document first.
 *
 * SCOPE: the document also specifies six SUB PAGES (one per standard), each
 * with its own SEO title, H1 and two paragraphs. Those are separate pages by
 * the document's own structure and are NOT built here — this file covers the
 * main page only. The sub-page copy is left in the document rather than
 * duplicated into this one.
 *
 * `logoKey` values resolve against src/data/certificationLogos.js, and `image`
 * paths point at the illustrations already in
 * public/assets/certification-process. No new artwork was introduced.
 */

export const isoSeo = {
  title:
    "ISO Certification Consultancy Services | ISO 9001, 14001, 45001 & More | MBA Solutions",
  description:
    "MBA Solutions provides professional ISO certification consultancy including ISO 9001, ISO 14001, ISO 45001, ISO 22000, ISO 27001 and ISO 50001 implementation support.",
  slug: "/iso-certification-services",
};

export const heroContent = {
  h1: "Professional ISO Certification Consultancy Services",
  subtitle:
    "Helping Businesses Build Stronger Management Systems With International Standards",
  paragraphs: [
    "MBA Solutions helps organizations achieve internationally recognized ISO certifications through expert consultancy, implementation support, documentation development, training and audit preparation.",
    "Our consultants work with businesses across different industries to develop practical management systems that improve quality, safety, efficiency, sustainability and business performance.",
    "Whether you are seeking your first certification or improving an existing management system, MBA Solutions provides complete support throughout your certification journey.",
  ],
};

export const benefitsLabel = "Benefits:";

export const servicesContent = {
  title: "Our ISO Certification Services",
  standards: [
    {
      id: "iso-9001",
      logoKey: "iso-9001",
      title: "ISO 9001 Certification",
      subtitle: "Quality Management System",
      paragraphs: [
        "ISO 9001 is the world’s most recognized quality management standard designed to help organizations improve processes, customer satisfaction and operational efficiency.",
        "MBA Solutions supports businesses in implementing ISO 9001 systems that create consistent processes, better performance and continuous improvement.",
      ],
      benefits: [
        "Improved product and service quality",
        "Increased customer confidence",
        "Better process control",
        "Reduced operational issues",
        "Continuous improvement culture",
      ],
    },
    {
      id: "iso-14001",
      logoKey: "iso-14001",
      title: "ISO 14001 Certification",
      subtitle: "Environmental Management System",
      paragraphs: [
        "ISO 14001 helps organizations manage environmental responsibilities and reduce their environmental impact through effective management practices.",
        "MBA Solutions assists companies in developing environmental management systems aligned with international requirements.",
      ],
      benefits: [
        "Improved environmental performance",
        "Better resource efficiency",
        "Reduced environmental risks",
        "Support for sustainability goals",
        "Enhanced brand reputation",
      ],
    },
    {
      id: "iso-45001",
      logoKey: "iso-45001",
      title: "ISO 45001 Certification",
      subtitle: "Occupational Health & Safety Management System",
      paragraphs: [
        "ISO 45001 provides a framework for creating safer workplaces and managing occupational health and safety risks.",
        "MBA Solutions helps organizations implement effective safety management systems to protect employees and improve workplace performance.",
      ],
      benefits: [
        "Reduced workplace hazards",
        "Improved employee safety",
        "Better legal compliance",
        "Lower operational risks",
        "Stronger safety culture",
      ],
    },
    {
      id: "iso-22000",
      logoKey: "iso-22000",
      title: "ISO 22000 Certification",
      subtitle: "Food Safety Management System",
      paragraphs: [
        "ISO 22000 helps food businesses identify, control and manage food safety risks throughout their supply chain.",
        "MBA Solutions provides consultancy for food manufacturers, processors and related businesses to establish effective food safety systems.",
      ],
      benefits: [
        "Improved food safety controls",
        "Compliance with international requirements",
        "Increased buyer confidence",
        "Better risk management",
        "Access to global markets",
      ],
    },
    {
      id: "iso-27001",
      logoKey: "iso-27001",
      title: "ISO 27001 Certification",
      subtitle: "Information Security Management System",
      paragraphs: [
        "ISO 27001 helps organizations protect sensitive information and manage cybersecurity risks through structured security controls.",
        "MBA Solutions supports businesses in developing information security management systems that protect valuable data assets.",
      ],
      benefits: [
        "Improved information protection",
        "Reduced security risks",
        "Better customer trust",
        "Strong data management practices",
        "Compliance with security requirements",
      ],
    },
    {
      id: "iso-50001",
      logoKey: "iso-50001",
      title: "ISO 50001 Certification",
      subtitle: "Energy Management System",
      paragraphs: [
        "ISO 50001 helps organizations improve energy efficiency, reduce energy consumption and manage energy performance.",
        "MBA Solutions supports businesses in implementing energy management systems that contribute to cost savings and sustainability.",
      ],
      benefits: [
        "Reduced energy costs",
        "Improved energy efficiency",
        "Lower environmental impact",
        "Better resource management",
        "Sustainability improvement",
      ],
    },
  ],
};

export const processContent = {
  title: "Our ISO Certification Process",
  subtitle: "A Complete Roadmap From Assessment To Certification",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "1. Initial Consultation",
      description:
        "Understanding your business, processes and certification requirements.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "2. Gap Assessment",
      description: "Reviewing your current system against ISO requirements.",
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      title: "3. Documentation Development",
      description: "Preparing required policies, procedures and records.",
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      title: "4. Implementation Support",
      description:
        "Helping your team apply the management system effectively.",
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      title: "5. Internal Audit",
      description:
        "Checking system effectiveness and preparing for certification.",
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      title: "6. Certification Audit Support",
      description:
        "Supporting your organization during the final audit process.",
    },
  ],
};

export const whyChooseContent = {
  title: "Why Choose MBA Solutions For ISO Certification?",
  reasons: [
    "Experienced ISO Consultants",
    "Practical Business-Focused Approach",
    "Complete Implementation Support",
    "Industry-Specific Solutions",
    "Certification Audit Preparation",
  ],
};

export const industriesContent = {
  title: "Industries We Support",
  intro: "MBA Solutions provides ISO consultancy for:",
  industries: [
    { icon: "food", name: "Food & Beverage" },
    { icon: "textile", name: "Textile & Garments" },
    { icon: "manufacturing", name: "Manufacturing" },
    { icon: "it", name: "IT & Software" },
    { icon: "engineering", name: "Engineering" },
    { icon: "hospitality", name: "Hospitality" },
    { icon: "export", name: "Export Businesses" },
    { icon: "service", name: "Service Organizations" },
  ],
};

export const finalCtaContent = {
  title: "Ready To Achieve Your ISO Certification?",
  paragraph:
    "Partner with MBA Solutions and build a management system that supports compliance, efficiency and business growth.",
  cta: {
    label: "Get Your ISO Certification Consultation Today",
    to: "/contact",
  },
};
